import React from "react";
import { RenderMediaContent } from "src/components/RenderMediaContent";
import twitch_embeds from "../../assets/setup/twitch_embeds.png";
import { InfoAlert } from "src/components/InfoAlert";

// component props interface
interface Props {
  isMobileView: boolean;
}

export const TwitchEmbeds: React.FC<Props> = ({ isMobileView }) => (
  <div className="flex flex-col gap-y-4 mx-auto px-4 max-w-screen-sm">
    <p>
      Create a SAMMI button that shows a Twitch embed (Video, Chat, or Video&nbsp;+&nbsp;Chat).
      You can log in, type in chat, and use all chat functions while authenticated.
    </p>

    <InfoAlert message="Twitch embeds are not available on iOS." />

    {/* responsive image */}
    <div className="overflow-x-auto">
      <RenderMediaContent
        src={twitch_embeds}
        /* desktop 800 px, mobile full width */
        width={isMobileView ? "100%" : "800px"}
      />
    </div>

    <section className="mt-4">
      <h2 className="text-lg font-semibold">How to create a Twitch Embed Button</h2>

      {/* step list */}
      <ul className="list-disc list-inside space-y-2">
        <li>
          Use the&nbsp;
          <strong>
            <a href="https://christinak.itch.io/deck-hopper-advanced">
              Deck&nbsp;Hopper Advanced
            </a>
          </strong>
          &nbsp;extension → <b>Create&nbsp;Embed&nbsp;Button</b> premade button
          and follow the in‑button instructions.
        </li>

        <li>Or build it manually:</li>
      </ul>

      {/* manual sub‑steps */}
      <ol className="list-decimal list-inside ml-6 space-y-2">
        <li>Create a new button in SAMMI&nbsp;Core.</li>
        <li>
          Right‑click the button → <b>Edit&nbsp;Init&nbsp;Variables</b> (set it
          persistent while editing if necessary).
        </li>
        <li>
          Add this object to the init variables:
          <pre className="bg-gray-800 p-2 rounded mt-2 overflow-x-auto text-sm whitespace-pre-wrap break-all">
            <code>
{`{
  "dh_styles": {
    "dh_embed": {
      "channel": "yourtwitchchannelusername",
      "type": "video"
    }
  }
}`}
            </code>
          </pre>
          Replace <code>yourtwitchchannelusername</code> (lower‑case) and set{" "}
          <code>type</code> to <code>video</code>, <code>chat</code>, or{" "}
          <code>video-chat</code>.
        </li>
        <li>Save the variables and the deck. Deck Hopper reloads with the embed.</li>
      </ol>
    </section>

    {/* notes */}
    <section className="mt-4">
      <h2 className="text-lg font-semibold">Important Note</h2>
      <p>
        <b>On Android:</b> Twitch blocks normal login inside embeds. Go to{" "}
        <b>Menu → Settings → Log&nbsp;into&nbsp;Twitch</b> and sign in there.
        The session persists.
      </p>
      <p className="mt-2">
        <b>On Desktop:</b> Sign in directly in the embed; the session also
        persists.
      </p>
    </section>
  </div>
);
